import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from 'src/app/shared/services/common.service';
import { ConfigService } from 'src/app/initializer/config.service';
import { InvoiceDetailService } from '../invoice-detail.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormControl, FormGroup } from '@angular/forms';
import { IFilterParams, IMatDailogData, IPayload, ISelectedGP, ITransactionList, Ivehicles, TransactionData } from '../invoice.interface';


@Component({
  selector: 'app-transaction-attachment-popup',
  templateUrl: './transaction-attachment-popup.component.html',
  styleUrls: ['./transaction-attachment-popup.component.scss']
})
export class TransactionAttachmentPopupComponent implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  isLoading = true;
  attachementLoading = false;
  isTableLoading = true;
  displayedColumns: string[] = [
    'bookingDate',
    'bookingText',
    'amount',
    'countaryparty',
    'bank',
    'linked',
  ];
  defaultSortBy = 'bookingDate';
  defaultOrderBy: SortDirection = 'desc';
  search: string;
  selectedTransactionIds: TransactionData[] = [];
  dataSource: MatTableDataSource<ITransactionList> = new MatTableDataSource<ITransactionList>([]);
  private invoiceSearchSubject = new Subject<boolean>();
  filters: BehaviorSubject<any> = new BehaviorSubject<IFilterParams>({});
  public sort: MatSort;
  totalItems = 0;
  page: number = 1;
  pageSize: number = 10;
  maxDate: Date = new Date();
  @ViewChild(MatSort, { static: false })
  set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      this.dataSource.sort = this.sort;
    }
  }
  selectedVehicle: Ivehicles | undefined;
  selectedGP: ISelectedGP;
  scrollTableLoading = false;
  payload: IPayload = {
    transactionType: []
  };

  constructor(
    public invoiceDetailService: InvoiceDetailService,
    public dialogRef: MatDialogRef<TransactionAttachmentPopupComponent>,
    public commonService: CommonService,
    private config: ConfigService,
    @Inject(MAT_DIALOG_DATA) public data: IMatDailogData
  ) {
  }
  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  ngOnInit() {
    this.payload.transactionType.push(this.data.invoiceType);
    this.filters.next({
      sortBy: this.defaultSortBy,
      order: this.defaultOrderBy,
    });
    this.invoiceSearchSubject.pipe(debounceTime(1000)).subscribe(flag => {
      flag && this.searchFilter();
    });

    if (this.data.gpId && this.data.vehicleId) {
      const baseconfigData = this.config.baseData.data;
      this.selectedGP = baseconfigData.gps.find((item: any) => item.id === this.data.gpId);
      this.selectedVehicle = this.selectedGP.vehicles.find(
        (item: Ivehicles) => item.id === this.data.vehicleId
      );
    }
  }

  searchFilter() {
    const filterValue = (this.search || '').trim().toLowerCase();
    const filter = this.filters.getValue();
    this.filters.next({
      ...filter,
      sortBy: this.defaultSortBy,
      order: this.defaultOrderBy,
      search: filterValue,
    });
  }

  searchHandler() {
    this.dataSource.data = [];
    this.page = 1;
    this.isTableLoading = true;
    this.invoiceSearchSubject.next(true);
    this.selectedTransactionIds = [];
  }
  ngAfterViewInit() {
    this.filters.subscribe((d) => {
      this.fetchAttachInvoices();
    });
  }

  fetchAttachInvoices(scrollLoading = false) {
    const currentFilters = this.filters.getValue();
    
    // If this is not a scroll loading (i.e., new search/filter), reset the data
    if (!scrollLoading) {
      this.dataSource.data = [];
      this.page = 1;
    }
    
    this.isLoading = !(this.dataSource.data.length > 0 || currentFilters?.search || currentFilters?.startDate);
    this.scrollTableLoading = scrollLoading;
    this.isTableLoading = !scrollLoading;

    this.invoiceDetailService
      .getTransactions(this.data.vehicleId, this.page, this.pageSize, currentFilters, this.payload).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          if (data) {
            const parsedResponse = JSON.parse(JSON.stringify(data));
            
            // For scroll loading (pagination), append data. For new searches, replace data
            if (scrollLoading) {
              this.dataSource.data = [...this.dataSource.data, ...parsedResponse.data.transactions];
            } else {
              this.dataSource.data = parsedResponse.data.transactions;
            }
            
            this.totalItems = parsedResponse.data.count;
            
            // Only increment page if we got data
            if (parsedResponse.data.transactions && parsedResponse.data.transactions.length > 0) {
              this.page++;
            }
            
            this.isTableLoading = false;
            this.scrollTableLoading = false;
          } else {
            // Handle empty response
            if (!scrollLoading) {
              this.dataSource.data = [];
            }
            this.isTableLoading = false;
            this.scrollTableLoading = false;
          }
        },
        error: error => {
          console.error('Failed to fetch attached invoices:', error);
          // Clear data on error for new searches
          if (!scrollLoading) {
            this.dataSource.data = [];
          }
          this.isTableLoading = false;
          this.scrollTableLoading = false;
        },
        complete: () => {
          this.isLoading = false;
          this.isTableLoading = false;
          this.scrollTableLoading = false;
        },
      });
  }

  linkTransaction() {
    if (this.selectedTransactionIds.length > 0) {
      this.attachementLoading = true;
      const transactionIds = this.selectedTransactionIds.map(element => element.transactionId);
      if (this.data.editInvoice) {
        this.dialogRef.close({ status: 1, selectedTransactionList: this.selectedTransactionIds });
        this.commonService.successNotification('Transactions linked successfully');
        return;
      }
      const params: any = {};
      params.transactionIds = transactionIds;
      this.invoiceDetailService.linkInvoiceToTransaction(this.data.invoiceId, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            this.attachementLoading = false;
             if (data.status == 'success') {
                this.commonService.successNotification(
                  'Transactions linked successfully'
                );
                this.dialogRef.close({status: 1, selectedTransactionList: this.selectedTransactionIds});
                }
              },
              error: error => {
                this.commonService.errorNotification(
                  'Failed to link Transactions. Please try again later.'
                );
              },
            });
        }
  }

  getAmountClass(amount: number): string {
    if (amount < 0) {
      return 'negative-amt';
    } else if (amount > 0) {
      return 'positive-amt';
    }
    return '';
  }

  sortChange() {
    this.dataSource.data = [];
    this.page = 1;
    this.selectedTransactionIds = [];
    const sortBy = this.sort.active;
    const order = this.sort.direction;
    this.filters.next({
      ...this.filters.getValue(),
      sortBy,
      order,
    });
  }

  clearFilter(filterKey: string) {
    this.dataSource.data = [];
    this.selectedTransactionIds = [];
    this.page = 1;
    const currentFilters = this.filters.getValue();
    const updatedFilters = { ...currentFilters };
    if (filterKey === 'dateRange') {
      this.range.reset();
      delete updatedFilters.startDate;
      delete updatedFilters.endDate;
    }
    if (filterKey === 'search') {
      this.search = '';
      delete updatedFilters.search;
    }
    this.filters.next(updatedFilters);
  }
  dateRangeFilter(startDate: HTMLInputElement, endDate: HTMLInputElement) {
    if (startDate?.value && endDate?.value) {
      const startDateString = this.formateDate(
        this.range.value.start
      );
      const endDateString = this.formateDate(this.range.value.end);
      this.filters.next({
        ...this.filters.getValue(),
        startDate: startDateString,
        endDate: endDateString,
      });
    }
  }
  
  selectionChangeValue(event: MatCheckboxChange, element: TransactionData) {
    if (event.checked) {
      this.selectedTransactionIds.push(element);
    } else {
      this.selectedTransactionIds = this.selectedTransactionIds.filter((selectedData: any) => selectedData.transactionId !== element.transactionId);
    }
  }
  onTableScroll(event: any) {
    const tableViewHeight = event.target.offsetHeight;
    const tableScrollHeight = event.target.scrollHeight;
    const scrollLocation = event.target.scrollTop;
    const buffer = 200;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    const loading = this.scrollTableLoading || this.isTableLoading;
    if (scrollLocation > limit && !loading && this.dataSource.data.length < this.totalItems) {
      this.fetchAttachInvoices(true);
    }
  }
  formateDate(value: any) {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}/${month}/${day}`;
    return formattedDate;

  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
